import { FloorPlanProductSettings, useFloorPlanConfig } from '../../steps/FloorPlanConfig'

import { BinaryButtonSwitch } from 'components/common/BinaryButtonSwitch'
import { FC } from 'react'
import { Label } from 'components/common/Label'
import { MUIInputField } from 'components/common/MUIInputField'
import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

interface Props {
  /** Id of product for which settings are to be recorder */
  productId: number
  /** Whether fields should be disabled */
  disabled?: boolean
}

/** Settings for floor plan editing settings for product listing content */
export const FloorPlanEditingProductSettings: FC<Props> = ({
  productId,
  disabled = false,
}) => {
  const { t } = useTranslation(['purchase_flow'])

  const {
    setFloorPlanProductSettings,
    floorPlanProductSettings,
  } = useFloorPlanConfig()

  const updateEntry = (id: number, value: Partial<FloorPlanProductSettings>) => {
    setFloorPlanProductSettings(previous => previous.set(
      id,
      {
        ...(previous.get(id) ?? {
          drawOnlySpecificUnit: undefined,
          drawOnlySpecificUnitComment: '',
        }),
        ...value,
      }
    ))
  }

  return (
    <Stack gap={1}>

      <Label text={t('floor_plan_step.product_config.specificity_label')} required />

      <BinaryButtonSwitch
        value={floorPlanProductSettings.get(productId)?.drawOnlySpecificUnit ?? undefined}
        onChange={(value) => updateEntry(
          productId,
          !!value
            ? {
              drawOnlySpecificUnit: value
            }
            : {
              drawOnlySpecificUnit: value,
              drawOnlySpecificUnitComment: '',
            }
        )}
        falseDisplayText={t('floor_plan_step.product_config.specificity_entire_plan')}
        trueDisplayText={t('floor_plan_step.product_config.specificity_specific_unit')}
        disabled={disabled}
      />

      {!!(floorPlanProductSettings.get(productId)?.drawOnlySpecificUnit) &&
        <>
          <Label text={t('floor_plan_step.product_config.specificity_comment_label')} required />

          <MUIInputField
            value={floorPlanProductSettings.get(productId)?.drawOnlySpecificUnitComment ?? ''}
            onChange={(e) => {
              updateEntry(productId, { drawOnlySpecificUnitComment: e.target.value })
            }}
            disabled={disabled}
            isMultiline
            maxLength={60}
          />
        </>
      }

    </Stack>
  )
}
